import React from 'react';
import { Input, Textarea } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
  BESPOKE_INPUT_COMPACT_CLASS,
  BESPOKE_TEXTAREA_COMPACT_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';
import { FieldTemplatePicker } from '../../FieldTemplatePicker';
import { useHasFieldTemplates } from '../../FormTemplatesContext';
import { CmsFieldError } from '../../CmsFieldError';
import { useCmsQuestionError } from '../../cmsFieldErrors';

export interface OASISTextRenderProps {
  value: string;
  onChange: (v: string) => void;
  label: string;
}

export interface OASISTextProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  className?: string;
  style?: React.CSSProperties;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  /** Render a textarea instead of a single-line input */
  multiline?: boolean;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  inputClassName?: string;
  children?: (props: OASISTextRenderProps) => React.ReactNode;
}

export function OASISText({
  qid,
  labelOverride,
  className,
  style,
  hideLabel,
  innerClassName,
  labelClassName,
  multiline,
  controlClassName,
  inputClassName,
  children,
}: OASISTextProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const cmsError = useCmsQuestionError(qid);
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);
  const hasTemplates = useHasFieldTemplates(qid);

  const value = (answers[qid] as string) ?? '';
  const onChange = (v: string) => onAnswerChange(qid, v);
  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;
  const resolvedControlClassName = resolveControlClassName(
    controlClassName,
    inputClassName
  );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      {children ? (
        children({ value, onChange, label })
      ) : (
        <div className={innerClassName ?? BESPOKE_FIELD_STACK_GAP_CLASS}>
          {/* Offered whether or not the label is shown: a compact layout that
              hides the prompt still holds the answer a template writes, and
              tying the picker to the label would drop it from exactly the
              dense layouts where retyping the paragraph hurts most.

              The row is skipped entirely when there is no label and no
              template, rather than rendered empty, because an empty element
              still consumes a gap in this `space-y` stack and would loosen
              every compact field that has no template. */}
          {(!hideLabel || hasTemplates) && (
            <div className='flex items-start justify-between gap-2'>
              {!hideLabel && (
                <label
                  className={joinClasses(
                    BESPOKE_FIELD_LABEL_CLASS,
                    labelClassName
                  )}
                >
                  {label}
                </label>
              )}
              <FieldTemplatePicker
                qid={qid}
                currentValue={value}
                onInsert={onChange}
                disabled={inheritedDisabled}
              />
            </div>
          )}
          {multiline ? (
            <Textarea
              disabled={inheritedDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={joinClasses(
                BESPOKE_TEXTAREA_COMPACT_CLASS,
                resolvedControlClassName
              )}
            />
          ) : (
            <Input
              disabled={inheritedDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={joinClasses(
                BESPOKE_INPUT_COMPACT_CLASS,
                resolvedControlClassName
              )}
            />
          )}
        </div>
      )}
      <CmsFieldError fieldId={`oasis-q-${qid}`} message={cmsError} />
    </div>
  );
}
